/**
 * ATL-962 (client half; the range/self-heal work it grew out of is ATL-958) — the per-item
 * verdict of a `POST /products/stock/bulk` response, and the ONE way a failed item is reported.
 *
 * The backend half is `ProductsService.updateStockBulk`'s per-store failure attribution
 * (apps/atlas-backend/src/modules/products/products.service.ts): together they are what makes a
 * 200 carrying failed items readable as the partial failure it is.
 *
 * Why this is its own (leaf) module rather than living in `stockMirror.ts`: BOTH the live mirror
 * and the outbox REPLAY need it, and `stockMirror` already imports `getOutboxSnapshot` from
 * `dualWriteOutbox`. Putting the verdict here keeps the dependency a tree — this file imports
 * nothing but Sentry — instead of adding a module-init cycle between the two.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * THE DEFECT THIS EXISTS TO CLOSE
 *
 * `/products/stock/bulk` answers **200 even when individual items fail** (an explicit contract —
 * `@HttpCode(200)` on products.controller.ts, so one bad store never drops the others). The
 * outbox's entire success test, however, was "the replay did not throw":
 *
 *     await typedClient.replayRequest(entry.request)
 *     await removeEntry(entry.id)
 *
 * `replayRequest` returns the parsed body and never looks at it. So a replay that failed every
 * one of its items returned 200, the entry was DELETED, and nothing was logged: no console line,
 * no Sentry event, no drop record. The stock stayed wrong in Supabase and the evidence was gone.
 *
 * The old code justified that as "a deterministic per-item failure (e.g. unknown store) converges
 * after one replay — the replay's 200 purges the entry". That justification is false for the
 * causes actually measured (issue `stock-mirror-item-failed`, 35/35 events classified): 34 were
 * `Product not found for ID: <id>` and one was `numeric field overflow`. Neither converges by
 * being retried; the replay reproduced the identical failure and then destroyed the record of it.
 */

import * as Sentry from '@sentry/react-native'

export interface BulkStockItemResult {
  productId: string
  storeId: string
  ok: boolean
  error?: string
}

/** The URL the bulk stock mirror replays under — the key the outbox recognises an entry by. */
export const BULK_STOCK_MIRROR_URL = '/products/stock/bulk'

/**
 * Does this queued request carry the per-item contract above?
 *
 * Matched on method + url rather than on the outbox label, because the label (`products.updateStock`)
 * is also worn by entries persisted by OLDER bundles that used the pre-ATL-765 single-store
 * `PATCH /products/:id/stock` shape, whose 200 has no `results[]` to inspect.
 */
export function isBulkStockMirrorRequest(request: {
  method?: string
  url?: string
}): boolean {
  return request?.method === 'POST' && request?.url === BULK_STOCK_MIRROR_URL
}

/**
 * The items a bulk-stock response reports as failed.
 *
 * A body that is not the expected shape yields `[]` — "no evidence of failure" — deliberately:
 * inventing a failure from an unrecognised body would re-queue a write that may well have landed,
 * and absolute stock semantics make a spurious retry harmless only because it is a no-op, not
 * because it is free. A genuinely broken response shape is a different defect, and the transport
 * layer (Zod response validation) is where it belongs.
 */
export function failedBulkStockItems(response: unknown): BulkStockItemResult[] {
  const results = (response as { results?: unknown } | null | undefined)?.results
  if (!Array.isArray(results)) return []
  return results.filter(
    (r): r is BulkStockItemResult =>
      !!r && typeof r === 'object' && (r as { ok?: unknown }).ok === false,
  )
}

/**
 * Report items the bulk stock call individually rejected, one warning per store.
 *
 * `phase` distinguishes the FIRST failure from a failed retry, and they are fingerprinted apart on
 * purpose: a live failure says "this store did not land yet" (the outbox will retry it), while a
 * replay failure says "it still has not landed after a retry" — which, before ATL-962, was
 * reported nowhere at all. Keeping the live fingerprint byte-identical preserves the history of
 * the existing issue.
 */
export function reportFailedStockMirrorItems(
  productId: string,
  results: readonly BulkStockItemResult[] | undefined,
  phase: 'live' | 'replay' = 'live',
): void {
  for (const result of results ?? []) {
    if (result.ok) continue
    const detail = {
      productId: result.productId,
      storeId: result.storeId,
      error: String(result.error ?? 'unknown').slice(0, 200),
      phase,
    }
    const suffix =
      phase === 'replay'
        ? ' — this is a RETRY of an earlier failure, so the divergence has persisted'
        : ''
    // eslint-disable-next-line no-console
    console.warn(
      `[dualWrite] products.updateStock bulk item FAILED (${phase}) for store ${result.storeId} — ` +
        `Firestore stock changed but Supabase was not updated for this store${suffix}`,
      JSON.stringify(detail),
    )
    Sentry.captureMessage(
      phase === 'replay'
        ? 'bulk stock mirror item failed again on replay'
        : 'bulk stock mirror item failed',
      {
        level: 'warning',
        tags: {
          supabase_dual_write_error: true,
          dual_write: 'products.updateStock',
          dual_write_module: 'products',
          dual_write_op: 'updateStock',
          stock_mirror_phase: phase,
        },
        fingerprint:
          phase === 'replay'
            ? ['stock-mirror-item-failed', 'replay']
            : ['stock-mirror-item-failed'],
        contexts: { stock_mirror: { ...detail, outboxProductId: productId } },
      },
    )
  }
}
